'use client'
import { useMemo } from 'react'
import type { CartItem, DiscountDTO } from 'ecosystem'
import { sumCart, sumCartBeforeQuantityDiscount } from 'shared-utils'
import { useVatMode } from '../../common/hooks/useVatMode'
import { applyDiscountToCart } from './utils'

/**
 * Resolves cart totals at render time based on the active VAT mode.
 *
 * The Redux cart slice persists a single gross `cartTotal` (used internally for
 * change detection, GA events, etc). For display we need the *currently
 * applicable* total — gross for anonymous/CONSUMER + toggle-off, net for
 * BUSINESS or toggle-on. This hook recomputes from the live cart items each
 * render so the VAT toggle takes effect instantly without touching Redux.
 *
 * Two reductions can be in play, and they are reported separately because a
 * shopper reads them as separate things: the code they typed in, and the volume
 * discount their quantities earned. They stack in that order — the ladder
 * scores against the post-code price — which is the order
 * `orderLineValueCalculator` prices a checkout line in, so the summary and the
 * payment provider land on the same number.
 */
export const useCartTotals = (
  cartItems: CartItem[],
  cartDiscountCode: DiscountDTO | null
): {
  excludeVat: boolean
  cartTotal: number
  cartTotalDiscount: number
  cartQuantityDiscount: number
} => {
  const { excludeVat } = useVatMode()

  const cartTotal = useMemo(() => sumCart(cartItems, excludeVat), [cartItems, excludeVat])

  // The basis the ladder actually applies to at checkout.
  const codeAppliedItems = useMemo(
    () =>
      cartDiscountCode
        ? (applyDiscountToCart(cartItems, cartDiscountCode) ?? cartItems)
        : cartItems,
    [cartItems, cartDiscountCode]
  )

  const cartTotalDiscount = useMemo(() => {
    if (!cartDiscountCode) return 0

    // Both sides measured before the ladder, so this is the code's share alone.
    return Math.max(
      sumCartBeforeQuantityDiscount(cartItems, excludeVat) -
        sumCartBeforeQuantityDiscount(codeAppliedItems, excludeVat),
      0
    )
  }, [cartItems, codeAppliedItems, cartDiscountCode, excludeVat])

  const cartQuantityDiscount = useMemo(
    () =>
      Math.max(
        sumCartBeforeQuantityDiscount(codeAppliedItems, excludeVat) -
          sumCart(codeAppliedItems, excludeVat),
        0
      ),
    [codeAppliedItems, excludeVat]
  )

  return { excludeVat, cartTotal, cartTotalDiscount, cartQuantityDiscount }
}
